import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ParentCardNoCol from '../../common/ParentCardNoCol';
import PHIAuditHeader from './PHIAuditHeader';
import PHIAuditLoader from './PHIAuditLoader';
import PHIAuditEmptyState from './PHIAuditEmptyState';
import PHIAuditSummary from './PHIAuditSummary';
import PHIAuditFindings from './PHIAuditFindings';

export const PHIAudit = ({ projectId, version, actions, audit }) => {
  const [loading, setLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  useEffect(() => {
    if (audit && typeof audit === 'object') {
      setAuditResult(audit);
    } else {
      setAuditResult(null);
    }
  }, [audit]);

  const runProjectAudit = async () => {
    try {
      setLoading(true);

      const response = await actions.runProjectPHIAudit({
        projectId,
        version,
      });

      if (!response || response.code !== 200) {
        toast.error(
          (response && (response.message || response.error)) || 'Failed to run PHI audit',
        );
        return;
      }

      if (!response.data || typeof response.data !== 'object') {
        toast.error('PHI audit completed but returned invalid data.');
        return;
      }

      setAuditResult(response.data);

      toast.success('PHI audit completed successfully');
    } catch (error) {
      console.error('Failed to run PHI audit:', error);
    } finally {
      setLoading(false);
    }
  };

  const safeAudit = auditResult && typeof auditResult === 'object' ? auditResult : null;

  const summary =
    safeAudit && safeAudit.summary && typeof safeAudit.summary === 'object'
      ? safeAudit.summary
      : {};

  const flaggedCollections =
    safeAudit && Array.isArray(safeAudit.flaggedCollections) ? safeAudit.flaggedCollections : [];

  const flaggedPages =
    safeAudit && Array.isArray(safeAudit.flaggedPages) ? safeAudit.flaggedPages : [];

  const hasCollectionFindings = flaggedCollections.some((collection) => {
    if (!collection || typeof collection !== 'object') {
      return false;
    }

    const fields = Array.isArray(collection.flaggedFields) ? collection.flaggedFields : [];

    const filters = Array.isArray(collection.flaggedFilters) ? collection.flaggedFilters : [];

    return fields.length > 0 || filters.length > 0;
  });

  const hasPageFindings = flaggedPages.length > 0;

  const hasFindings = hasCollectionFindings || hasPageFindings;

  const lastAuditedAt = useMemo(() => {
    if (!safeAudit || !safeAudit.auditedAt) {
      return null;
    }

    const date = new Date(safeAudit.auditedAt);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleString();
  }, [safeAudit]);

  return (
    <ParentCardNoCol title="PHI Audit" icon="phiAudit" bgColor="drapcode-violet">
      <div>
        <PHIAuditHeader
          hasFindings={hasFindings}
          lastAuditedAt={lastAuditedAt}
          loading={loading}
          hasAudit={!!safeAudit}
          onRunAudit={runProjectAudit}
          projectId={projectId}
        />
        {loading && <PHIAuditLoader />}
        {!safeAudit && !loading && <PHIAuditEmptyState />}
        {safeAudit && (
          <div
            style={{
              opacity: loading ? 0.45 : 1,
              pointerEvents: loading ? 'none' : 'auto',
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <PHIAuditSummary summary={summary} />
            <PHIAuditFindings
              hasFindings={hasFindings}
              hasCollectionFindings={hasCollectionFindings}
              hasPageFindings={hasPageFindings}
              flaggedCollections={flaggedCollections}
              flaggedPages={flaggedPages}
            />
          </div>
        )}
        <style>
          {`
            @keyframes phiAuditSpin {
              from {
                transform: rotate(0deg);
              }

              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </div>
    </ParentCardNoCol>
  );
};
